import type { IContractRepository } from "@/application/ports/repositories";
import type {
	ContractListResponse,
	ContractListParams,
	ContractStatsParams,
	ContractStatsResponse,
	DocumentResponse,
	ResendDocumentResponse,
	VoidContractRequest,
	VoidContractResponse,
} from "@/domain/contract";
import { apiClient, ApiClientError } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class ContractRepository implements IContractRepository {
	async listContracts(
		params: ContractListParams = {},
	): Promise<ContractListResponse> {
		const logger = getLogger();
		try {
			const searchParams = new URLSearchParams();
			if (params.limit) searchParams.append("limit", params.limit.toString());
			if (params.offset)
				searchParams.append("offset", params.offset.toString());
			if (params.contract_type)
				searchParams.append("contract_type", params.contract_type);
			if (params.status) searchParams.append("status", params.status);
			if (params.investor_uuid)
				searchParams.append("investor_uuid", params.investor_uuid);
			if (params.promoter_uuid)
				searchParams.append("promoter_uuid", params.promoter_uuid);
			if (params.document_uuid)
				searchParams.append("document_uuid", params.document_uuid);
			if (params.search) searchParams.append("search", params.search);
			if (params.sort_by) searchParams.append("sort_by", params.sort_by);
			if (params.sort_direction)
				searchParams.append("sort_direction", params.sort_direction);

			const response = await apiClient.get<ContractListResponse>(
				`/contracts?${searchParams.toString()}`,
			);
			return response.data;
		} catch (error) {
			logger.error("Error listing contracts:", error);
			throw error;
		}
	}

	async getContractStats(
		params: ContractStatsParams = {},
	): Promise<ContractStatsResponse> {
		const logger = getLogger();
		try {
			const searchParams = new URLSearchParams();
			if (params.contract_type)
				searchParams.append("contract_type", params.contract_type);
			if (params.status) searchParams.append("status", params.status);
			if (params.investor_uuid)
				searchParams.append("investor_uuid", params.investor_uuid);
			if (params.promoter_uuid)
				searchParams.append("promoter_uuid", params.promoter_uuid);

			const response = await apiClient.get<ContractStatsResponse>(
				`/contracts/stats?${searchParams.toString()}`,
			);
			return response.data;
		} catch (error) {
			logger.error("Error getting contract stats:", error);
			throw error;
		}
	}

	async getContractDocument(contractUuid: string): Promise<DocumentResponse> {
		const response = await apiClient.get<DocumentResponse>(
			`/contracts/${contractUuid}/document`,
		);
		return response.data;
	}

	async downloadContractDocument(contractUuid: string): Promise<void> {
		const logger = getLogger();
		try {
			const documentResponse = await this.getContractDocument(contractUuid);

			const response = await fetch(documentResponse.signed_url, {
				method: "GET",
				mode: "cors",
				credentials: "omit",
				headers: { Accept: "application/pdf" },
			});

			if (!response.ok) {
				throw new ApiClientError({
					message: `Failed to download document: ${response.status} ${response.statusText}`,
					code: "DOWNLOAD_ERROR",
					status: response.status,
				});
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = blobUrl;
			link.download = `contract-${contractUuid}.pdf`;
			link.style.display = "none";
			document.body.appendChild(link);
			link.click();

			setTimeout(() => {
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			}, 100);
		} catch (error) {
			logger.error("Error downloading contract document:", error);
			throw error;
		}
	}

	async resendContract(
		investmentUuid: string,
		documentUuid: string,
	): Promise<ResendDocumentResponse> {
		const response = await apiClient.post<ResendDocumentResponse>(
			`/investments/${investmentUuid}/documents/${documentUuid}/actions/resend`,
			{},
		);
		return response.data;
	}

	async voidContract(
		investmentUuid: string,
		documentUuid: string,
	): Promise<VoidContractResponse> {
		const requestBody: VoidContractRequest = { document_uuid: documentUuid };
		const response = await apiClient.post<VoidContractResponse>(
			`/investments/${investmentUuid}/actions/void-contract`,
			requestBody,
		);
		return response.data;
	}

	async deleteContract(
		investmentUuid: string,
		documentUuid: string,
	): Promise<void> {
		await apiClient.delete(
			`/investments/${investmentUuid}/documents/${documentUuid}`,
		);
	}
}
